import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { useFeaturesReady } from "../featuresReady/FeaturesReadyContext";
import { useRuntimeConstants } from "../runtimeConstants/RuntimeConstantsContext";
import { getFeature } from "../../utils/features";
import { useProduct } from "../product/ProductContext";
import * as mySizeProfileApi from "../../api/mySizeProfileApi";
import type {
  MySizeProfile,
  SizeGuideRecommendation,
  Brand,
  LegLength,
} from "../../api/mySizeProfileApiTypes";
import type {
  MySizeProfileFormState,
  MySizeProfileContextType,
} from "./MySizeProfileContext.types";
import {
  getCustomerId,
  getCustomerIsAuthenticated,
} from "../../utils/identity";
import { logError, ERROR_TYPE } from "../../utils/newRelicLogger";
import {
  getMySizeProfileDismissed,
  setMySizeProfileDismissed,
} from "../../utils/mySizeProfileStorage";
import { showSizeProfileCreatedNotification } from "../../utils/sizeProfileNotification";
import { fireMspOnboardingCompleted } from "../../utils/marketingTracking/marketingTrackingEvents";
export type { MySizeProfileFormState } from "./MySizeProfileContext.types";

const emptyFormState: MySizeProfileFormState = {
  selectedBrand: null,
  brandId: null,
  brandSize: "",
  legLengthValue: "",
};

const emptyMySizeProfileContext: MySizeProfileContextType = {
  mySizeProfile: null,
  sizeGuideRecommendation: null,
  loading: false,
  error: null,
  dismissed: false,
  isEligible: false,
  isAuthenticated: false,
  isMySizeProfileEnabled: false,
  formState: emptyFormState,
  brands: [],
  brandsLoading: false,
  brandsError: null,
  legLengths: [],
  saveMySizeProfile: async () => {
    throw new Error("MySizeProfileContextProvider is not available");
  },
  loadBrands: async () => {},
  restoreFormStateFromProfile: async () => {},
  updateFormState: () => {},
  clearFormState: () => {},
  dismissMySizeProfile: () => {},
};

const MySizeProfileContext = createContext<
  MySizeProfileContextType | undefined
>(undefined);

interface MySizeProfileContextProviderProps {
  children: ReactNode;
}

export const MySizeProfileContextProvider = ({
  children,
}: MySizeProfileContextProviderProps) => {
  const { areFeaturesReady } = useFeaturesReady() as {
    areFeaturesReady: boolean;
  };
  const { webContext } = useRuntimeConstants() as {
    webContext?: {
      browseSizeSchema?: string;
      browseLanguage?: string;
    };
  };
  const { product } = useProduct();
  const browseSizeSchema = webContext?.browseSizeSchema ?? "UK";
  const browseLanguage = webContext?.browseLanguage;
  const gender = product?.gender?.toLowerCase() ?? "";
  const productTypeName = product?.productType?.name?.toLowerCase() ?? "";
  const meetsMySizeProfileConditions =
    areFeaturesReady &&
    !!product &&
    gender.includes("women") &&
    productTypeName.includes("jeans");

  const [mySizeProfile, setMySizeProfile] = useState<MySizeProfile | null>(
    null,
  );
  const [sizeGuideRecommendation, setSizeGuideRecommendation] =
    useState<SizeGuideRecommendation | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [dismissed, setDismissed] = useState<boolean>(() =>
    getMySizeProfileDismissed(),
  );
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [formState, setFormState] =
    useState<MySizeProfileFormState>(emptyFormState);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [brandsLoading, setBrandsLoading] = useState<boolean>(false);
  const [brandsError, setBrandsError] = useState<Error | null>(null);
  const [legLengths, setLegLengths] = useState<LegLength[]>([]);
  const [isMySizeProfileEnabled, setIsMySizeProfileEnabled] =
    useState<boolean>(false);
  const hasTrackedOnboardingCompletedRef = useRef<boolean>(false);
  const isEligible = isMySizeProfileEnabled;

  const populateFormStateFromProfile = useCallback(
    async (profile: MySizeProfile) => {
      try {
        const brand = brands.find((b) => b.brandId === profile.brandId);

        if (brand) {
          const sizeMapping = brand.sizeMappings.find(
            (sm) => sm.sizeProfileId === profile.sizeProfileId,
          );
          const brandSize = sizeMapping?.brandSize || "";
          const legLengthValue = profile.legLength
            ? String(profile.legLength)
            : "";

          const populatedFormState = {
            selectedBrand: brand.brandName,
            brandId: brand.brandId,
            brandSize,
            legLengthValue,
          };

          setFormState(populatedFormState);
        }
      } catch (error) {
        logError(ERROR_TYPE.UserActionFailed, {
          action: "populateFormStateFromProfile",
          caller: "MySizeProfileContext",
          apiName: "MySizeProfile",
          message: (error as Error).message,
        });
      }
    },
    [brands],
  );
  const fetchAndSetSizeGuideRecommendation = useCallback(
    async (customerId: string, profile: MySizeProfile | null) => {
      if (!profile || !product?.id) {
        setSizeGuideRecommendation(null);
        return null;
      }

      const sizeGuideData = await mySizeProfileApi.fetchCustomerSizeGuide(
        customerId,
        product.id,
        browseLanguage,
      );
      setSizeGuideRecommendation(sizeGuideData);
      return sizeGuideData;
    },
    [product?.id, browseLanguage],
  );

  useEffect(() => {
    if (!meetsMySizeProfileConditions) {
      setMySizeProfile(null);
      setSizeGuideRecommendation(null);
      setLoading(false);
      setError(null);
      setIsAuthenticated(false);
      setIsMySizeProfileEnabled(false);
      return;
    }

    let isCancelled = false;

    const fetch = async () => {
      if (isCancelled) return;

      setLoading(true);
      setError(null);

      try {
        const isLoggedIn = await getCustomerIsAuthenticated();
        if (isCancelled) return;

        setIsAuthenticated(isLoggedIn);

        if (!isLoggedIn) {
          setIsMySizeProfileEnabled(false);
          setMySizeProfile(null);
          setSizeGuideRecommendation(null);
          return;
        }

        const productHasMapping = await mySizeProfileApi.fetchProductHasMapping(
          product.id,
        );
        if (isCancelled) return;

        if (!productHasMapping) {
          setIsMySizeProfileEnabled(false);
          setMySizeProfile(null);
          setSizeGuideRecommendation(null);
          return;
        }

        const isFeatureEnabled =
          getFeature("pdp_my_size_profile")?.isEnabled() ?? false;

        setIsMySizeProfileEnabled(isFeatureEnabled);

        if (!isFeatureEnabled) {
          setMySizeProfile(null);
          setSizeGuideRecommendation(null);
          return;
        }

        const authenticatedCustomerId = await getCustomerId();

        const data = await mySizeProfileApi.fetchMySizeProfile(
          authenticatedCustomerId,
        );
        if (isCancelled) return;

        setMySizeProfile(data);

        if (data) {
          await populateFormStateFromProfile(data);
        }

        await fetchAndSetSizeGuideRecommendation(authenticatedCustomerId, data);
      } catch (err) {
        if (isCancelled) return;

        setError(err as Error);
        setMySizeProfile(null);
        setSizeGuideRecommendation(null);
        setIsMySizeProfileEnabled(false);
        logError(ERROR_TYPE.UserActionFailed, {
          action: "fetchMySizeProfile",
          caller: "MySizeProfileContext",
          apiName: "MySizeProfile",
          message: (err as Error).message,
        });
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetch();

    return () => {
      isCancelled = true;
    };
  }, [
    fetchAndSetSizeGuideRecommendation,
    populateFormStateFromProfile,
    meetsMySizeProfileConditions,
    areFeaturesReady,
    gender,
    productTypeName,
    product?.id,
  ]);

  const loadBrandsData = async () => {
    if (
      !isAuthenticated ||
      !isMySizeProfileEnabled ||
      !browseSizeSchema ||
      !browseLanguage
    ) {
      return;
    }

    // Only fetch if brands aren't already cached
    if (brands.length > 0) {
      return;
    }

    setBrandsLoading(true);
    setBrandsError(null);

    try {
      const response = await mySizeProfileApi.fetchBrands(
        browseSizeSchema,
        browseLanguage,
      );
      setBrands(response.brands);
      setLegLengths(response.legLengths);
    } catch (error) {
      const err = error as Error;
      setBrandsError(err);
      logError(ERROR_TYPE.UserActionFailed, {
        action: "fetchBrands",
        caller: "MySizeProfileContext",
        apiName: "MySizeProfile",
        message: err.message,
      });
    } finally {
      setBrandsLoading(false);
    }
  };

  const saveSizeProfile = async (
    sizeProfileId: string,
    legLength: number | null,
    formStateToSave: MySizeProfileFormState,
  ): Promise<MySizeProfile> => {
    setLoading(true);
    setError(null);

    try {
      const brandId = formStateToSave?.brandId;
      const brandSize = formStateToSave?.brandSize;
      if (!brandId) {
        throw new Error("Brand ID is required to save size profile");
      }
      if (!brandSize) {
        throw new Error("Brand size is required to save size profile");
      }

      const currentCustomerId = await getCustomerId();

      const isNewProfile = mySizeProfile === null;
      await mySizeProfileApi.saveMySizeProfile(
        currentCustomerId,
        sizeProfileId,
        legLength,
        brandId,
        brandSize,
      );
      const data = await mySizeProfileApi.fetchMySizeProfile(currentCustomerId);
      if (!data) {
        throw new Error("Failed to fetch updated profile after save");
      }

      setMySizeProfile(data);
      const sizeGuideData = await fetchAndSetSizeGuideRecommendation(
        currentCustomerId,
        data,
      );

      setFormState(formStateToSave);
      if (isNewProfile && sizeGuideData?.sizeGuideMessage) {
        showSizeProfileCreatedNotification();
      }

      if (isNewProfile && !hasTrackedOnboardingCompletedRef.current) {
        hasTrackedOnboardingCompletedRef.current = true;
        fireMspOnboardingCompleted({ product });
      }

      return data;
    } catch (err) {
      setError(err as Error);
      logError(ERROR_TYPE.UserActionFailed, {
        action: "saveMySizeProfile",
        caller: "MySizeProfileContext",
        apiName: "MySizeProfile",
        message: (err as Error).message,
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateFormState = (
    partialFormState: Partial<MySizeProfileFormState>,
  ) => {
    setFormState((prevState) => ({ ...prevState, ...partialFormState }));
  };

  const clearFormState = () => {
    setFormState(emptyFormState);
  };

  const restoreFormStateFromProfile = useCallback(async () => {
    if (mySizeProfile && brands.length > 0) {
      await populateFormStateFromProfile(mySizeProfile);
    }
  }, [mySizeProfile, brands, populateFormStateFromProfile]);

  const saveMySizeProfile: MySizeProfileContextType["saveMySizeProfile"] =
    saveSizeProfile;

  const dismissMySizeProfile = (): void => {
    setDismissed(true);
  };

  useEffect(() => {
    if (dismissed) {
      setMySizeProfileDismissed(true);
    }
  }, [dismissed]);

  const value: MySizeProfileContextType = {
    mySizeProfile,
    sizeGuideRecommendation,
    loading,
    error,
    dismissed,
    isEligible,
    isAuthenticated,
    isMySizeProfileEnabled,
    formState,
    brands,
    brandsLoading,
    brandsError,
    legLengths,
    saveMySizeProfile,
    loadBrands: loadBrandsData,
    restoreFormStateFromProfile,
    updateFormState,
    clearFormState,
    dismissMySizeProfile,
  };

  return (
    <MySizeProfileContext.Provider value={value}>
      {children}
    </MySizeProfileContext.Provider>
  );
};

export const useMySizeProfile = (): MySizeProfileContextType => {
  const context = useContext(MySizeProfileContext);
  if (!context) {
    throw new Error(
      "useMySizeProfile must be used within MySizeProfileContextProvider",
    );
  }
  return context;
};

export const useOptionalMySizeProfile = (): MySizeProfileContextType => {
  const context = useContext(MySizeProfileContext);
  return context ?? emptyMySizeProfileContext;
};
